import React, { useState, useEffect } from "react";
import { ClipboardIcon } from "@heroicons/react/16/solid";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import Modal from "../../../componentLibrary/Modal";
import Button from "../../../componentLibrary/Button";
import { useNotification } from "../../../hooks/useNotification";
import { LessonI, PageI, ComponentI, ComponentEnum } from "../../../types";
import { parsePreviousAnswers } from "../utils/parsePreviousAnswers";

type AddVariableModalProps = {
  isModalOpen: boolean;
  closeModal: () => void;
  lesson: LessonI;
  currentPageId: string;
};

export default function AddVariableModal({
  isModalOpen,
  closeModal,
  lesson,
  currentPageId,
}: AddVariableModalProps) {
  const { notify } = useNotification();
  const [selectedPage, setSelectedPage] = useState<ComboboxOptionI>({
    value: "",
    label: "",
  });
  const [selectedVariable, setSelectedVariable] = useState<ComboboxOptionI>({
    value: "",
    label: "",
  });
  const [variableDetails, setvariableDetails] = useState(null);
  const [pageList, setPageList] = useState<PageI[]>([]);
  const [pageVariables, setPageVariables] = useState<ComponentI[]>([]);

  const getPageComponents = (page: PageI) => {
    let filteredVariables =
      page?.components.filter((component) => "variable" in component.content) ??
      [];

    const filteredLogicComponents =
      page?.components
        .filter((component) => component.type === ComponentEnum.LOGIC_COMPONENT)
        .flatMap((component) =>
          component.content.componentsList?.filter(
            (c: ComponentI) => "variable" in c.content,
          ),
        ) ?? [];

    filteredVariables = [...filteredVariables, ...filteredLogicComponents];

    return filteredVariables;
  };

  useEffect(() => {
    if (lesson && currentPageId) {
      const currentPage = lesson.pages.findIndex(
        (page: PageI) => page._id === currentPageId,
      );
      if (currentPage !== -1) {
        setPageList(lesson.pages.slice(0, currentPage + 1));
      }
    }
    return () => {
      setPageList([]);
      setPageVariables([]);
      setSelectedPage({ value: "", label: "" });
      setSelectedVariable({ value: "", label: "" });
      setvariableDetails(null);
    };
  }, [currentPageId, lesson]);

  useEffect(() => {
    setSelectedVariable({ value: "", label: "" });
    setvariableDetails(null);
    if (selectedPage.value !== "" && lesson) {
      const page = lesson.pages.find(
        (page: PageI) => page._id === selectedPage.value,
      );

      const filteredVariables = getPageComponents(page);

      setPageVariables(filteredVariables);
    } else {
      setPageVariables([]);
    }
  }, [selectedPage, lesson]);

  const handleSubmit = () => {
    if (selectedVariable.value !== "") {
      navigator.clipboard
        .writeText(`{{${selectedVariable.value}}}`)
        .then(() => {
          notify("success", "Variable copied to clipboard!");
          closeModal();
        })
        .catch(() => {
          notify("error", "Failed to copy to clipboard");
        });
    }
  };

  const renderVariableDetails = (component: ComponentI) => {
    switch (component.type) {
      case ComponentEnum.INPUT:
        return (
          <div>
            <div>
              <span className="font-bold">Label: </span>{" "}
              <span>
                {parsePreviousAnswers(component.content.label || "", {})}
              </span>
            </div>
            <div>
              <span className="font-bold">Placeholder: </span>{" "}
              <span>
                {parsePreviousAnswers(component.content.placeholder || "", {})}
              </span>
            </div>
          </div>
        );
      case ComponentEnum.MULTIPLE_CHOICE:
        return (
          <div>
            <div>
              <span className="font-bold">Label: </span>{" "}
              <span>
                {parsePreviousAnswers(component.content.label || "", {})}
              </span>
            </div>
            {Object.entries(component.content)
              .filter(([key]) => key.startsWith("choice_"))
              .map(([key, value]) => (
                <div key={key}>
                  <span className="font-bold">
                    Choice {key.split("_")[1]}:{" "}
                  </span>
                  <span>
                    {parsePreviousAnswers((value as string) || "", {})}
                  </span>
                </div>
              ))}
            <div>
              <span className="font-bold">Has Other: </span>
              <span>{component.content.other ? "Yes" : "No"}</span>
            </div>
            <div>
              <span className="font-bold">Multi Select: </span>
              <span>{component.content.msq ? "Yes" : "No"}</span>
            </div>
          </div>
        );
      case ComponentEnum.RATING_SCALE:
        return (
          <div>
            <div>
              <span className="font-bold">Label: </span>
              <span>
                {parsePreviousAnswers(component.content.label || "", {})}
              </span>
            </div>
            <div>
              <span className="font-bold">Minimum Label: </span>
              <span>
                {parsePreviousAnswers(component.content.minimumLabel || "", {})}
              </span>
            </div>
            <div>
              <span className="font-bold">Center Label: </span>
              <span>
                {parsePreviousAnswers(component.content.centerLabel || "", {})}
              </span>
            </div>
            <div>
              <span className="font-bold">Maximum Label: </span>
              <span>
                {parsePreviousAnswers(component.content.maximumLabel || "", {})}
              </span>
            </div>
            <div>
              <span className="font-bold">Number of Steps: </span>
              <span>{component.content.numberOfSteps || 5}</span>
            </div>
          </div>
        );
      case ComponentEnum.USER_LIST:
        return (
          <div>
            <div>
              <span className="font-bold">Label: </span>
              <span>
                {parsePreviousAnswers(component.content.label || "", {})}
              </span>
            </div>
            {Object.entries(component.content)
              .filter(([key]) => key.startsWith("placeholder_"))
              .map(([key, value]) => (
                <div key={key}>
                  <span className="font-bold">
                    Placeholder {key.split("_")[1]}:{" "}
                  </span>
                  <span>
                    {parsePreviousAnswers((value as string) || "", {})}
                  </span>
                </div>
              ))}
          </div>
        );
      default:
        return <></>;
    }
  };

  return (
    <div>
      <Modal
        title="Add Variable"
        size="md"
        isOpen={isModalOpen}
        onCloseModal={closeModal}
        height="50vh"
      >
        <div className="mb-2">
          <Combobox
            label="Pick a Page"
            placeholder="Select a Page"
            options={pageList.map((page: PageI) => ({
              label: page.title,
              value: page._id,
              className:
                getPageComponents(page).length === 0
                  ? "text-gray"
                  : "font-semibold",
            }))}
            onChange={(option) => {
              if (option) setSelectedPage(option);
              else setSelectedPage({ value: "", label: "" });
            }}
            value={selectedPage}
            disabled={false}
          />
        </div>
        <div className="mb-2">
          <Combobox
            placeholder={
              pageVariables?.length === 0
                ? "No variables found"
                : "Select a Variable"
            }
            options={pageVariables.map((variable: ComponentI) => ({
              label: variable.content.variable,
              value: variable.content.variable,
            }))}
            onChange={(option) => {
              if (option) {
                const variable = pageVariables.find(
                  (variable: ComponentI) =>
                    variable.content.variable === option.value,
                );
                setvariableDetails(variable);
                setSelectedVariable(option);
              } else {
                setvariableDetails(null);
                setSelectedVariable({ value: "", label: "" });
              }
            }}
            value={selectedVariable}
            disabled={pageVariables?.length === 0}
          />
        </div>
        {variableDetails && (
          <>
            <div className="mb-2 text-sm">
              <div>
                <span className="font-bold">Type: </span>
                <span>{variableDetails.type}</span>
              </div>
              {renderVariableDetails(variableDetails)}
            </div>
            <Button className="w-full mt-6" onClick={handleSubmit}>
              <ClipboardIcon className="w-6 h-6 mr-2" /> Copy
            </Button>
          </>
        )}
      </Modal>
    </div>
  );
}
